import {
  Grid,
  List,
  ListItem,
  ListItemText,
  Theme,
  withStyles,
  WithStyles
} from '@material-ui/core'
import i18next, { TFunction } from 'i18next'
import { FC, Fragment } from 'react'
import { getFirstCharCapitalized } from '../../../libs/strings'
import { MuiClasses } from '../../../types'
import { formatStringIfDate } from '../../../utils/dates'

const styles = ({ breakpoints }: Theme) => ({
  listItems: {
    padding: '0',
    margin: '0',
    color: '#0000008A'
  },
  listItemsTitle: {
    padding: '0',
    margin: '0',
    'text-transform': 'uppercase',
    fontSize: '0.7rem !important',
    '& p': {
      color: '#000000DE'
    }
  },
  spouseLifeSituation: {
    '& span': {
      fontSize: '1.2rem'
    }
  }
})

export interface CustomListProps extends WithStyles {
  classes: MuiClasses
  TitleTypography?: JSX.Element
  data: any
  informationOrder: string[]
  translationPath: string
  t: i18next.TFunction
  multiSelectPaths?: { [key: string]: string }
  hideListItemLogic?: (key: string, value: string) => boolean
  capitalizeFirstLetter?: boolean
}

interface ListItemProps {
  t: TFunction
  objectValue: string
  capitalizeFirstLetter?: boolean
}

interface MultiSelectTranslationProps extends ListItemProps {
  multiSelectPath: string
}

const getTranslatedMultiValue = ({
  t,
  multiSelectPath,
  objectValue,
  capitalizeFirstLetter
}: MultiSelectTranslationProps) => {
  const translatedValue = t(`${multiSelectPath}.${objectValue}`)
  return capitalizeFirstLetter
    ? getFirstCharCapitalized(translatedValue)
    : translatedValue
}

const getCapitalized = ({
  t,
  objectValue,
  capitalizeFirstLetter
}: ListItemProps) => {
  const translatedValue = t(`${formatStringIfDate(objectValue)}`)
  return capitalizeFirstLetter
    ? getFirstCharCapitalized(translatedValue)
    : translatedValue
}

const CustomList: FC<CustomListProps> = ({
  classes,
  t,
  TitleTypography,
  data,
  informationOrder,
  translationPath,
  multiSelectPaths,
  hideListItemLogic,
  capitalizeFirstLetter
}) => {
  const renderSwitch = ({
    key,
    objectValue,
    multiSelectPath,
    isErrorOrNoAnswer
  }: any) => {
    switch (key) {
      case 'phoneNumber':
        return (
          <Grid item xs={12} md={6} lg={6}>
            <a
              style={{
                textDecoration: 'none',
                fontFamily: 'CircularRegular'
              }}
              href={`tel:+${objectValue}`}
            >
              +{objectValue}
            </a>
          </Grid>
        )
      case 'pets':
        return (
          <Grid item xs={12} md={6} lg={6}>
            <ListItemText
              primary={getCapitalized({
                t,
                objectValue,
                capitalizeFirstLetter
              })}
              className={classes.listItems}
            />
          </Grid>
        )
      default:
        return (
          <Grid item xs={12} md={6} lg={6}>
            {multiSelectPath && !isErrorOrNoAnswer ? (
              <>
                <ListItemText
                  primary={getTranslatedMultiValue({
                    t,
                    objectValue,
                    capitalizeFirstLetter,
                    multiSelectPath
                  })}
                  className={classes.listItems}
                />
              </>
            ) : (
              <>
                <ListItemText
                  primary={getCapitalized({
                    t,
                    objectValue,
                    capitalizeFirstLetter
                  })}
                  className={classes.listItems}
                />
              </>
            )}
          </Grid>
        )
    }
  }
  return (
    <Fragment>
      {TitleTypography}
      <List
        component={'div' as any}
        disablePadding
        className={classes.innerList}
      >
        {informationOrder.map((key: string, index: number) => {
          const isErrorOrNoAnswer = hideListItemLogic
            ? data[key] === 'error' || hideListItemLogic(key, data[key])
            : data[key] === 'error'
          let objectValue = isErrorOrNoAnswer ? undefined : data[key]
          const multiSelectPath = multiSelectPaths && multiSelectPaths[key]
          let newPath = ''
          if (
            (key === 'livelihood' || key === 'spouseLivelihood') &&
            Array.isArray(data[key])
          ) {
            newPath = `newApplication.livelihood`
            objectValue = data[key]
              .map((value: string) =>
                value === 'myAnswer'
                  ? data['livelihoodCustom']
                  : t(`newApplication.livelihoodOptions.${value}`)
              )
              .join(', ')
          }
          if (key === 'movingAbout') {
            newPath = 'application.list.readyForMoveDate'
          }
          if (
            key === 'lifeSituationCustom' ||
            key === 'spouseLifeSituationCustom' ||
            key === 'spouseLifeSituation'
          ) {
            newPath = `application.list.lifeSituation`
          }

          if (key === 'depositPaymentMethodCustom') {
            newPath = 'application.list.depositPaymentMethod'
          }

          if (objectValue === 'nextMonth') {
            return <></>
          }

          if (key === 'spouseCreditStatus') {
            newPath = 'application.list.creditStatus'
          }
          return (
            objectValue && (
              <>
                {key === 'spouseLifeSituation' && (
                  <ListItemText
                    primary={t('application.suposeTitle')}
                    className={classes.spouseLifeSituation}
                    style={{
                      margin: '10px 0'
                    }}
                  />
                )}
                <ListItem key={`${key}+${index}`} className={classes.listItems}>
                  <Grid
                    container
                    item
                    direction='row'
                    justify='space-between'
                    style={{
                      margin: '5px 0'
                    }}
                  >
                    <Grid item xs={12} md={6} lg={6}>
                      <ListItemText
                        secondary={
                          newPath !== ''
                            ? t(`${newPath}`)
                            : t(`${translationPath}${key}`)
                        }
                        className={classes.listItemsTitle}
                      />
                    </Grid>
                    {renderSwitch({
                      key,
                      objectValue,
                      multiSelectPath,
                      isErrorOrNoAnswer
                    })}
                  </Grid>
                </ListItem>
              </>
            )
          )
        })}
      </List>
    </Fragment>
  )
}

export default withStyles(styles)(CustomList)
